import { useEffect, useRef, useState } from "react";
import { Mic, Play, Pause, Loader2, Check, X } from "lucide-react";
import { BACKEND_URL } from "@/lib/api";

export const VoiceoverPanel = ({ voices = [], voiceover, onSave, onClear, disabled }) => {
  const [text, setText] = useState("");
  const [voice, setVoice] = useState(voices[0]?.id || "alloy");
  const [busy, setBusy] = useState(false);
  const [playing, setPlaying] = useState(null);
  const audioRef = useRef(null);

  useEffect(() => {
    if (voiceover) { setText(voiceover.text || ""); setVoice(voiceover.voice || voices[0]?.id || "alloy"); }
  }, [voiceover, voices]);
  useEffect(() => () => audioRef.current?.pause(), []);

  const preview = (id) => {
    const a = audioRef.current;
    if (!a) return;
    if (playing === id) { a.pause(); setPlaying(null); return; }
    a.src = `${BACKEND_URL}/api/tts/voices/${id}/preview`;
    setPlaying(id);
    a.play().catch((e) => { if (e?.name !== "AbortError") setPlaying(null); });
  };

  const save = async () => {
    if (!text.trim()) return;
    setBusy(true);
    try { await onSave(text.trim(), voice); } finally { setBusy(false); }
  };

  const changed = !voiceover || voiceover.text !== text.trim() || voiceover.voice !== voice;

  return (
    <div className="card-surface p-5 space-y-4" data-testid="voiceover-panel">
      <audio ref={audioRef} onEnded={() => setPlaying(null)} className="hidden" />
      <div className="flex items-center justify-between gap-2">
        <h3 className="text-sm font-semibold flex items-center gap-2"><Mic size={15} className="text-primary" /> AI Voiceover <span className="text-muted-foreground font-normal">(optional)</span></h3>
        {voiceover && (
          <button onClick={onClear} disabled={disabled || busy} className="text-xs font-mono text-muted-foreground hover:text-destructive flex items-center gap-1" data-testid="voiceover-remove">
            <X size={12} /> Remove
          </button>
        )}
      </div>
      <textarea
        value={text}
        maxLength={600}
        disabled={disabled}
        onChange={(e) => setText(e.target.value)}
        placeholder="Type what the voice should say — we'll narrate it over your reel and duck the music under it."
        rows={2}
        className="w-full rounded-xl border border-input bg-background p-3 text-sm resize-none"
        data-testid="voiceover-text"
      />
      <div className="flex flex-wrap gap-1.5" data-testid="voiceover-voices">
        {voices.map((v) => (
          <div key={v.id} className={`flex items-center gap-1 rounded-full border pl-1 pr-2 py-1 text-xs transition-colors ${voice === v.id ? "border-primary bg-primary/10" : "border-border"}`}>
            <button onClick={() => preview(v.id)} className="h-6 w-6 rounded-full bg-primary/15 text-primary grid place-items-center" data-testid={`voiceover-preview-${v.id}`} aria-label={`Preview ${v.label}`}>
              {playing === v.id ? <Pause size={11} /> : <Play size={11} className="ml-0.5" />}
            </button>
            <button onClick={() => setVoice(v.id)} disabled={disabled} className="font-medium" data-testid={`voiceover-voice-${v.id}`} title={v.desc}>{v.label}</button>
          </div>
        ))}
      </div>
      <div className="flex items-center justify-between gap-3">
        <p className="text-[11px] text-muted-foreground font-mono">{text.length}/600</p>
        <button onClick={save} disabled={disabled || busy || !text.trim() || !changed} className="btn-pill bg-primary text-primary-foreground px-4 py-2 text-xs font-semibold flex items-center gap-1.5 disabled:opacity-50" data-testid="voiceover-save">
          {busy ? <Loader2 size={13} className="animate-spin" /> : voiceover && !changed ? <Check size={13} /> : <Mic size={13} />}
          {voiceover ? (changed ? "Update voiceover" : "Voiceover added") : "Add voiceover"}
        </button>
      </div>
    </div>
  );
};
